import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { UserMinus } from 'lucide-react';

import type { InvitationRecord, TeamMemberRecord, TeamRecord } from '@/domain/teamTypes';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function TeamDetail({ team }: { team: TeamRecord }) {
  const { user } = useAuth();
  const isOwner = team.owner === user?.id;

  const [members, setMembers] = useState<TeamMemberRecord[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<InvitationRecord[]>([]);
  const [email, setEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [memberRecords, invitationRecords] = await Promise.all([
          pb
            .collection('team_members')
            .getFullList<TeamMemberRecord>({ filter: `team = "${team.id}"`, expand: 'user' }),
          isOwner
            ? pb.collection('invitations').getFullList<InvitationRecord>({
                filter: `team = "${team.id}" && status = "pending"`,
              })
            : Promise.resolve([]),
        ]);
        setMembers(memberRecords);
        setPendingInvitations(invitationRecords);
      } catch {
        toast.error('Impossible de charger les membres de cette équipe.');
      }
    })();
  }, [team.id, isOwner, refreshToken]);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsInviting(true);
    try {
      await pb.collection('invitations').create({
        team: team.id,
        email,
        invitedBy: user.id,
        status: 'pending',
      });
      setEmail('');
      toast.success('Invitation envoyée.');
      setRefreshToken((t) => t + 1);
    } catch {
      toast.error("Impossible d'envoyer l'invitation.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (member: TeamMemberRecord) => {
    try {
      await pb.collection('team_members').delete(member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      toast.success('Membre retiré.');
    } catch {
      toast.error('Impossible de retirer ce membre.');
    }
  };

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted-foreground">Membres</h3>
        <ul className="flex flex-col gap-1">
          {members.map((member) => (
            <li key={member.id} className="flex items-center justify-between text-sm">
              <span>
                {member.expand?.user?.name || member.expand?.user?.email || member.user}
                {member.user === team.owner && (
                  <span className="ml-2 text-xs text-muted-foreground">(propriétaire)</span>
                )}
              </span>
              {isOwner && member.user !== team.owner && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Retirer"
                  onClick={() => handleRemove(member)}
                >
                  <UserMinus />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {isOwner && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-muted-foreground">Inviter par email</h3>
          <form onSubmit={handleInvite} className="flex gap-2">
            <Input
              type="email"
              required
              placeholder="collegue@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" disabled={isInviting}>
              Inviter
            </Button>
          </form>
          {pendingInvitations.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
              {pendingInvitations.map((invitation) => (
                <li key={invitation.id}>{invitation.email} — en attente</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
